/**
 * Where the tip strikes the cue ball (UI-001).
 *
 * `spinX`/`spinY` are the tip's offset across the ball face in units of R, with the
 * centre at (0, 0): +x is right-hand english, +y is follow and −y is draw. The sim
 * caps the offset at `maxTipOffsetR` — beyond about half a radius a real cue miscues,
 * and `validateShot` rejects anything further out.
 *
 * Clamping happens here rather than at the control so the pad can be dragged past its
 * edge and simply stick there, which is how every spin control worth using behaves.
 */

export interface TipOffset {
  readonly spinX: number;
  readonly spinY: number;
}

export const CENTRE_BALL: TipOffset = { spinX: 0, spinY: 0 };

/**
 * Pull a tip offset back onto the playable disc, preserving its direction.
 *
 * Clamps to a CIRCLE, not a square: the corner of a square pad is a larger offset
 * than its edge, and the cap is on total distance from centre.
 *
 * ⚠️ Quantising is deliberately one-directional. Rounding a clamped offset to the
 * nearest 1e-4 can push it back OUT past the cap — (0.3536, 0.3536) is 0.50007R — and
 * `validateShot` would then reject a shot the player was allowed to make. Truncating
 * toward zero can only ever shrink it.
 */
export function clampTipOffset(spinX: number, spinY: number, maxOffsetR: number): TipOffset {
  if (!Number.isFinite(spinX) || !Number.isFinite(spinY)) return CENTRE_BALL;

  const distance = Math.sqrt(spinX * spinX + spinY * spinY);
  if (distance === 0) return CENTRE_BALL;

  const scale = distance > maxOffsetR ? maxOffsetR / distance : 1;
  return { spinX: quantise(spinX * scale), spinY: quantise(spinY * scale) };
}

/** True when the tip is at dead centre — the default, and what "reset" gives back. */
export function isCentreBall(offset: TipOffset): boolean {
  return offset.spinX === 0 && offset.spinY === 0;
}

/**
 * Four decimals, toward zero — finer than a thumb can place on a 96 px pad, and it
 * keeps the shot log readable.
 */
function quantise(value: number): number {
  // `+ 0` turns −0 into 0: a tap level with the centre gives −0 from `−dy / r`, and the
  // shot log should hold one canonical zero (UI-009).
  return Math.trunc(value * 1e4) / 1e4 + 0;
}

/**
 * The tip offset in words, for the spin ball's label and tooltip (UI-009: no visible
 * readout, whose changing width made the control jump).
 *
 * Pool's own vocabulary, not coordinates: a player thinks "a bit of right-hand follow",
 * not "spinX 0.3". Described to a tenth of a radius, which is about the resolution a
 * thumb can place and rather finer than the difference is worth naming.
 */
export function describeTipOffset(offset: TipOffset): string {
  if (isCentreBall(offset)) return 'Centre ball';

  const parts: string[] = [];
  const vertical = tenths(offset.spinY);
  const horizontal = tenths(offset.spinX);
  if (vertical !== '0.0') parts.push(`${vertical}R ${offset.spinY > 0 ? 'follow' : 'draw'}`);
  if (horizontal !== '0.0') parts.push(`${horizontal}R ${offset.spinX > 0 ? 'right' : 'left'}`);

  // Off centre, but by less than a tenth of a radius — "0.0R right" would read as a
  // contradiction, and "Centre ball" would be a lie.
  return parts.length === 0 ? 'Near centre' : parts.join(' · ');
}

function tenths(value: number): string {
  return (Math.round(Math.abs(value) * 10) / 10).toFixed(1);
}

/**
 * UI-009: how close to the middle a tap must land to mean "centre ball". The spin
 * ball sits inline beside Shoot and is small, so a tap on its middle is the reset.
 */
export const CENTRE_SNAP_R = 0.08;

/**
 * How much of the cue ball the spin ball shows, from its centre, in units of R (UI-014,
 * owner 2026-09-27: "the spin is so small, even for PC"). The whole ball face used to be
 * drawn, but a tip can only go within `maxTipOffsetR` (half of it), so only the middle
 * half of a small ball was usable. Now the drawn ball is that middle, magnified: the pad's
 * radius is SPIN_VIEW_R, and the miscue ring sits at 0.5 / 0.625 = 80% of it.
 */
export const SPIN_VIEW_R = 0.625;

/** Screen pixels per R on a spin ball drawn `padRadiusPx` across its radius. */
export function pixelsPerR(padRadiusPx: number): number {
  return padRadiusPx / SPIN_VIEW_R;
}

/**
 * A TAP on the spin ball: the tip goes where the finger landed. `dxPx`/`dyPx` are from
 * the ball's centre in screen pixels (+y down), `radiusPx` the pixels in one R
 * (`pixelsPerR`). A tap near the middle snaps to dead centre.
 */
export function tapOffset(
  dxPx: number,
  dyPx: number,
  radiusPx: number,
  maxOffsetR: number,
): TipOffset {
  if (!(radiusPx > 0)) return CENTRE_BALL;
  // Screen y grows downward; the ball face does not.
  const spinX = dxPx / radiusPx;
  const spinY = -dyPx / radiusPx;
  if (spinX * spinX + spinY * spinY < CENTRE_SNAP_R * CENTRE_SNAP_R) return CENTRE_BALL;
  return clampTipOffset(spinX, spinY, maxOffsetR);
}

/**
 * A DRAG on the spin ball: the tip moves from where it was, by the finger's movement
 * at half scale, so the finger never has to cover the spot it is setting (the same
 * idea as aiming, UI-008). Moving one ball-width sweeps the tip across the whole
 * playable disc. The finger may leave the ball and keep going; the tip sticks at the cap.
 */
export const DRAG_SCALE = 0.5;

export function dragOffset(
  from: TipOffset,
  dxPx: number,
  dyPx: number,
  radiusPx: number,
  maxOffsetR: number,
): TipOffset {
  if (!(radiusPx > 0)) return from;
  return clampTipOffset(
    from.spinX + (dxPx / radiusPx) * DRAG_SCALE,
    from.spinY - (dyPx / radiusPx) * DRAG_SCALE,
    maxOffsetR,
  );
}

/**
 * The spin ball's offset in the TABLE's frame (UI-016). The spin ball is drawn as the
 * player sees the cue ball, but an upright phone draws the table mirrored (L28, L32):
 * there, "right" on screen is the table's left. Side spin is flipped by the viewport's
 * handedness, exactly as fine adjust is; follow and draw are not, as a mirror does not
 * swap top and bottom. The shot log, and so the server, gets the table-frame value.
 */
export function spinForTable(offset: TipOffset, screenTurnSign: number): TipOffset {
  // `0 - x`, not `-x`: centre ball stays +0, never -0, so the shot log is unchanged.
  return screenTurnSign < 0 ? { spinX: 0 - offset.spinX, spinY: offset.spinY } : offset;
}
